import ReactDOM from "react-dom/client";
import "./index.css";
// Initialise i18next (detects + applies language) before anything renders.
import i18n from "./i18n";
import { RouterProvider } from "react-router-dom";
import { appRouter } from "./routes/Routes.tsx";
import { CrmProvider } from "./context/CrmContext.tsx";
import { ProfileProvider } from "./context/ProfileContext.tsx";
import { TeamScopeProvider } from "./context/TeamScopeContext.tsx";
import { CoachRailProvider } from "./context/CoachRailContext.tsx";
import { ThemeProvider } from "./components/theme-provider.tsx";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/query-client";
import { initGA, trackPageView } from "./lib/analytics";
import { getPageTitle } from "./lib/routeTitles";
import { TRPCProvider, trpcClient } from "./trpc/client";
import { initOpenPanelAnalytics } from "./analytics/openpanel-init";
import { track, LogEvents } from "./analytics/track";
import {
  stripSensitiveParamsFromBrowserUrl,
  sanitizeUrlParams,
} from "./lib/sanitize-url";
import { RemoteConfigProvider } from "./context/RemoteConfigContext.tsx";

// Remove sensitive query params (e.g. impersonation tokens) from the browser
// URL **before** any analytics SDK reads `window.location`.
stripSensitiveParamsFromBrowserUrl();

Sentry.init({
  dsn: "https://eb211213fac1c00a72b72a16fc231b74@o4508257702772736.ingest.de.sentry.io/4508257708408912",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  beforeSendTransaction(event) {
    if (event.transaction) {
      event.transaction = sanitizeUrlParams(event.transaction);
    }
    if (event.request?.url) {
      event.request.url = sanitizeUrlParams(event.request.url);
    }
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === "navigation") {
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = sanitizeUrlParams(breadcrumb.data.from);
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = sanitizeUrlParams(breadcrumb.data.to);
      }
    }
    return breadcrumb;
  },
});

// Initialize Google Analytics 4
initGA();

initOpenPanelAnalytics();
track({ event: LogEvents.AppBoot.name, channel: LogEvents.AppBoot.channel });

// Track initial page view (URL is already sanitised by stripSensitiveParamsFromBrowserUrl above)
const initialTitle = getPageTitle(window.location.pathname);
document.title = `${initialTitle} | Graftus`;
trackPageView(
  sanitizeUrlParams(window.location.pathname + window.location.search),
  initialTitle,
);

// Track page views on route changes
appRouter.subscribe((state) => {
  if (state.location) {
    const title = getPageTitle(state.location.pathname);
    document.title = `${title} | Graftus`;
    trackPageView(
      sanitizeUrlParams(state.location.pathname + state.location.search),
      title,
    );
  }
});

// Keep the tab title localized when the user switches language.
i18n.on("languageChanged", () => {
  const title = getPageTitle(appRouter.state.location.pathname);
  document.title = `${title} | Graftus`;
});

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  /*  <React.StrictMode> */
  <QueryClientProvider client={queryClient}>
    <RemoteConfigProvider>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <CrmProvider>
          <ProfileProvider>
            <TeamScopeProvider>
              <CoachRailProvider>
                <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
                  <RouterProvider router={appRouter} />
                </ThemeProvider>
              </CoachRailProvider>
            </TeamScopeProvider>
          </ProfileProvider>
        </CrmProvider>
      </TRPCProvider>
    </RemoteConfigProvider>
  </QueryClientProvider>,
  /*   </React.StrictMode> */
);
